/**
 * Opens a blank tab synchronously (so Safari does not block it) and returns
 * helpers to redirect it to a URL or close it once the async work completes.
 *
 * Must be called inside a user gesture (e.g. an onClick handler), otherwise
 * the browser will block the popup.
 *
 * Do NOT use this when the URL is already known at click time (e.g. a static
 * link on a button's onClick): in that case just call
 * `window.open(url, '_blank', 'noopener,noreferrer')` directly.
 *
 * @example
 * // Async case: the URL comes from a saga/API call → use this helper.
 * const handleClick = () => {
 *   const { redirect, close } = openTabForAsyncRedirect();
 *
 *   executeGetBillingLink({
 *     successSaga: ({ response }) => {
 *       if (response?.link) {
 *         redirect(response.link);
 *       } else {
 *         close();
 *       }
 *     },
 *     errorSaga: () => {
 *       close();
 *     },
 *   });
 * };
 *
 * <Button onClick={handleClick} />
 *
 * @example
 * // Sync case: the URL is already known → do NOT use this helper.
 * const handleClick = () => {
 *   window.open('https://example.com', '_blank');
 * };
 *
 * <Button onClick={handleClick} />
 */
export const openTabForAsyncRedirect = () => {
  const newTab = window.open('about:blank', '_blank');
  if (newTab) {
    newTab.opener = null;
  }

  const redirect = (url: string) => {
    if (newTab) {
      newTab.location.href = url;
    } else {
      window.location.href = url;
    }
  };

  const close = () => {
    newTab?.close();
  };

  return { redirect, close };
};
